import { injectable } from 'inversify'
import { jsonDateParser } from 'json-date-parser'

import { UniqueEntityID } from '../core'
import { Aircraft } from '../models/Aircraft'
import { FuelConsumptionUnit, SpeedUnit } from '../protocols/Unit'
import { SerializableState } from './SerializableState'

import { isSet } from '@/utils/validation'

export interface FlightStateProps {
  activeAircraft: Aircraft | null
  activeCruiseAltitude: number | null
  isCruiseAltitudeSuggested: boolean
  avgSpeed?: number
  avgFuelConsumption?: number
  speedUnit?: SpeedUnit
  fuelUnit?: FuelConsumptionUnit
}

const INITIAL_STATE: FlightStateProps = {
  activeAircraft: null,
  activeCruiseAltitude: null,
  isCruiseAltitudeSuggested: false,
  avgSpeed: null,
  avgFuelConsumption: null,
  speedUnit: 'kt',
  fuelUnit: 'l/h'
}

@injectable()
export class FlightState extends SerializableState<FlightStateProps> {
  constructor() {
    super(INITIAL_STATE)
  }

  getAvgSpeed(): number | null {
    if (isSet(this.getStateSnapshot().avgSpeed)) {
      return this.getStateSnapshot().avgSpeed
    }
    return null
  }

  setAvgSpeed(avgSpeed: number): void {
    this._updateStateProp('avgSpeed', avgSpeed)
  }

  getAvgFuelConsumption(): number | null {
    if (isSet(this.getStateSnapshot().avgFuelConsumption)) {
      return this.getStateSnapshot().avgFuelConsumption
    }
    return null
  }

  setAvgFuelConsumption(avgFuelConsumption: number): void {
    this._updateStateProp('avgFuelConsumption', avgFuelConsumption)
  }

  getSpeedUnit(): SpeedUnit | null {
    return this.getStateSnapshot().speedUnit ?? null
  }

  setSpeedUnit(speedUnit: SpeedUnit): void {
    this._updateStateProp('speedUnit', speedUnit)
  }

  getFuelUnit(): FuelConsumptionUnit | null {
    return this.getStateSnapshot().fuelUnit ?? null
  }

  setFuelUnit(fuelUnit: FuelConsumptionUnit): void {
    this._updateStateProp('fuelUnit', fuelUnit)
  }

  getActiveAircraft(): Aircraft | null {
    return this.getStateSnapshot().activeAircraft
  }

  setActiveAircraft(aircraft: Aircraft | null): void {
    this._updateStateProp('activeAircraft', aircraft)
  }

  getActiveCruiseAltitude(): number | null {
    return this.getStateSnapshot().activeCruiseAltitude
  }

  setActiveCruiseAltitude(cruiseAltitude: number | null): void {
    this._updateStateProp('activeCruiseAltitude', cruiseAltitude)
  }

  isCruiseAltitudeSuggested(): boolean {
    return this.getStateSnapshot().isCruiseAltitudeSuggested
  }

  setCruiseAltitudeSuggested(suggested: boolean): void {
    this._updateStateProp('isCruiseAltitudeSuggested', suggested)
  }

  toString(): string {
    const snapshot = this.getStateSnapshot()

    return JSON.stringify({
      activeAircraft: snapshot.activeAircraft?.toPojo() ?? null,
      activeCruiseAltitude: snapshot.activeCruiseAltitude,
      isCruiseAltitudeSuggested: snapshot.isCruiseAltitudeSuggested,
      avgSpeed: snapshot.avgSpeed,
      avgFuelConsumption: snapshot.avgFuelConsumption,
      speedUnit: snapshot.speedUnit,
      fuelUnit: snapshot.fuelUnit
    })
  }

  fromString(state: string): void {
    const parsedState = JSON.parse(state, jsonDateParser)

    this._updateState({
      activeAircraft: Aircraft.fromPojo(parsedState.activeAircraft),
      activeCruiseAltitude: parsedState.activeCruiseAltitude,
      isCruiseAltitudeSuggested: parsedState.isCruiseAltitudeSuggested,
      avgSpeed: parsedState.avgSpeed,
      avgFuelConsumption: parsedState.avgFuelConsumption,
      speedUnit: parsedState.speedUnit,
      fuelUnit: parsedState.fuelUnit
    })
  }

  getKey(): string {
    return 'flightState'
  }
}
